import { Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  HOURS_FORMATS,
  LOGO_CONTENT_TYPES,
  LOGO_MAX_BYTES,
  isValidIsoDate,
  type OrganizationLogo,
} from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Alert, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/misc';
import { NativeSelect } from '@/components/ui/native-select';
import {
  useOrganization,
  useRemoveOrganizationLogo,
  useUpdateOrganization,
  useUploadOrganizationLogo,
} from '@/lib/admin-queries';
import { describeError, fieldErrorsOf } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { useFormat } from '@/lib/use-format';
import { closedThrough } from '@/lib/use-timesheet-lock';
import { AdminPage } from './admin-ui';

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/** Organization defaults: name, week start, time zone, hours format and language. */
export function AdminSettingsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const organization = useOrganization();
  const update = useUpdateOrganization();
  const [form, setForm] = useState<{
    name: string;
    weekStartDay: number;
    defaultTimeZone: string;
    hoursFormat: string;
    locale: string;
    /** The closed period; empty means nothing is closed. */
    timesheetLockDate: string;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = fieldErrorsOf(error);

  // Seed the form once the settings arrive; after that it is the user's to edit.
  useEffect(() => {
    if (organization.data && !form) {
      setForm({
        name: organization.data.name,
        weekStartDay: organization.data.weekStartDay,
        defaultTimeZone: organization.data.defaultTimeZone,
        hoursFormat: organization.data.hoursFormat,
        locale: organization.data.locale,
        timesheetLockDate: organization.data.timesheetLockDate ?? '',
      });
    }
  }, [organization.data, form]);

  if (organization.isPending || !form) return <Spinner />;
  if (organization.isError)
    return <Alert variant="error">{describeError(organization.error, t)}</Alert>;

  const submit = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        name: form.name,
        weekStartDay: form.weekStartDay,
        defaultTimeZone: form.defaultTimeZone,
        hoursFormat: form.hoursFormat as (typeof HOURS_FORMATS)[number],
        locale: form.locale,
        // An empty field is an explicit null: the period is re-opened, not left as it was.
        timesheetLockDate: form.timesheetLockDate || null,
      });
      toast.success(t('admin.settings.saved'));
    } catch (e) {
      setError(e);
    }
  };

  return (
    <AdminPage title={t('admin.settings.title')} description={t('admin.settings.subtitle')}>
      <Card>
        <CardContent className="pt-6">
          <form
            className="max-w-lg space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Field label={t('admin.settings.name')} required error={fieldErrors.name}>
              {(props) => (
                <Input
                  {...props}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              )}
            </Field>
            <Field label={t('admin.settings.weekStartDay')} error={fieldErrors.weekStartDay}>
              {(props) => (
                <NativeSelect
                  {...props}
                  className="w-full"
                  value={String(form.weekStartDay)}
                  onChange={(e) => setForm({ ...form, weekStartDay: Number(e.target.value) })}
                >
                  {WEEKDAYS.map((day) => (
                    <option key={day} value={day}>
                      {t(`weekdays.${day}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field
              label={t('admin.settings.defaultTimeZone')}
              error={fieldErrors.defaultTimeZone}
              hint="IANA, e.g. Europe/Rome"
            >
              {(props) => (
                <Input
                  {...props}
                  value={form.defaultTimeZone}
                  onChange={(e) => setForm({ ...form, defaultTimeZone: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.settings.hoursFormat')} error={fieldErrors.hoursFormat}>
              {(props) => (
                <NativeSelect
                  {...props}
                  className="w-full"
                  value={form.hoursFormat}
                  onChange={(e) => setForm({ ...form, hoursFormat: e.target.value })}
                >
                  {HOURS_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {format === 'DECIMAL'
                        ? t('admin.settings.hoursDecimal')
                        : t('admin.settings.hoursHm')}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('admin.settings.locale')} error={fieldErrors.locale}>
              {(props) => (
                <Input
                  {...props}
                  value={form.locale}
                  onChange={(e) => setForm({ ...form, locale: e.target.value })}
                />
              )}
            </Field>
            {/* Last in the form, because it is the only setting here that changes what other
                people are allowed to do rather than how something is displayed. */}
            <Field
              label={t('admin.settings.timesheetLock')}
              hint={t('admin.settings.timesheetLockHelp')}
              error={fieldErrors.timesheetLockDate}
            >
              {(props) => (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    {...props}
                    type="date"
                    className="w-44"
                    value={form.timesheetLockDate}
                    onChange={(e) => setForm({ ...form, timesheetLockDate: e.target.value })}
                  />
                  {form.timesheetLockDate ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, timesheetLockDate: '' })}
                    >
                      {t('admin.settings.timesheetLockClear')}
                    </Button>
                  ) : null}
                </div>
              )}
            </Field>
            {/* Says it back as a last closed day. "Before 1 October" and "September is closed"
                are the same instruction, and only one of them is the one being typed. */}
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {isValidIsoDate(form.timesheetLockDate)
                ? t('admin.settings.timesheetLockEffect', {
                    date: formatDate(closedThrough(form.timesheetLockDate), fmt),
                  })
                : t('admin.settings.timesheetLockNone')}
            </p>

            {error && Object.keys(fieldErrors).length === 0 ? (
              <p className="text-sm text-destructive" role="alert">
                {describeError(error, t)}
              </p>
            ) : null}
            <Button type="submit" disabled={update.isPending}>
              {t('app.save')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <LogoCard logo={organization.data.logo} />
    </AdminPage>
  );
}

/**
 * The company logo.
 *
 * Its own card rather than a field in the form above, because it does not save with the rest: an
 * upload is a request of its own, and a form that saved five text fields and a file together
 * would have to explain which half failed.
 */
function LogoCard({ logo }: { logo: OrganizationLogo | null }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadOrganizationLogo();
  const remove = useRemoveOrganizationLogo();
  const [error, setError] = useState<string | null>(null);

  const choose = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    // Checked here only to answer instantly; the server checks everything again and is the one
    // that decides.
    if (file.size > LOGO_MAX_BYTES) {
      setError(t('admin.settings.logoTooBig', { max: LOGO_MAX_BYTES / 1024 / 1024 }));
      return;
    }
    try {
      await upload.mutateAsync(file);
      toast.success(t('admin.settings.logoSaved'));
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      // Let the same file be chosen again after a failure.
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>{t('admin.settings.logoTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="max-w-lg text-sm text-muted-foreground">{t('admin.settings.logoBody')}</p>

        {logo ? (
          <div className="flex items-center gap-4">
            {/* A checked background, so a transparent PNG is visibly transparent rather than
                looking like it has a white box around it. */}
            <div className="rounded-md border bg-[linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%),linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%)] bg-[length:12px_12px] bg-[position:0_0,6px_6px] p-3">
              <img
                src={logo.url}
                alt={t('admin.settings.logoAlt')}
                className="h-12 w-auto max-w-[260px] object-contain"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('admin.settings.logoSize', { width: logo.width, height: logo.height })}
            </p>
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">{t('admin.settings.logoNone')}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={input}
            type="file"
            accept={LOGO_CONTENT_TYPES.join(',')}
            aria-label={t('admin.settings.logoFile')}
            disabled={upload.isPending}
            className="text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          {upload.isPending ? <Spinner className="h-4 w-4" /> : null}
          {logo ? (
            <Button
              variant="outline"
              disabled={remove.isPending}
              onClick={() =>
                void remove.mutateAsync().then(
                  () => toast.success(t('admin.settings.logoRemoved')),
                  (e: unknown) => setError(describeError(e, t)),
                )
              }
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" /> {t('admin.settings.logoRemove')}
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              {t('admin.settings.logoHint')}
            </span>
          )}
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}
